import type { DispensationEvidenceModel } from "@/api/cle-api";
import EditIcon from "@/components/icons/EditIcon";
import { ModalProvider, ModalTrigger } from "@/components/Modal/Modal";
import { UserQueryContext } from "@/context/UserContext";
import { formatLessons } from "@/util/number";
import { useContext } from "react";

import { DetailItem } from "../person/id/Efteruddannelse/DetailItem";
import type { DecoratedPersonEfteruddannelse } from "../person/id/Efteruddannelse/decoratePeriod";
import { formatDispensationPeriodLabel } from "../person/id/Efteruddannelse/dispensationUtils";
import { EditDispensationModal } from "../person/id/Efteruddannelse/EditDispensationModal";

interface DispensationValidationProps {
  dispensation: DispensationEvidenceModel;
  periods: DecoratedPersonEfteruddannelse[];
  periodLabelLookup: Map<string, string>;
  userId: string;
  onDispensationUpdated?: (updated: DispensationEvidenceModel) => void;
}

export const DispensationValidation = ({
  dispensation,
  periods,
  periodLabelLookup,
  userId,
  onDispensationUpdated,
}: DispensationValidationProps) => {
  const { user, hasPermission } = useContext(UserQueryContext);
  // Reported (indberettet) evidence backs a signed declaration and is immutable;
  // the API refuses these edits with 409 EVIDENCE_ALREADY_REPORTED.
  const canEdit =
    hasPermission(user, "TilsynWrite") && !dispensation.isReported;

  const handleSave = (updated: DispensationEvidenceModel) => {
    onDispensationUpdated?.(updated);
  };

  return (
    <ModalProvider>
      <div className="c-card c-item-list">
        <div className="flex-100 d-flex justify-content-between flex-row align-items-center">
          <h3 className="mb-0">Dispensationsoplysninger</h3>
          {canEdit && (
            <ModalTrigger className="btn btn-primary btn--transparent has-icon px-2 edit-trigger">
              Redigér
              <EditIcon height={16} />
            </ModalTrigger>
          )}
        </div>
        <div className="d-flex flex-column gap-2">
          <DetailItem
            label="Efteruddannelsesperiode"
            value={formatDispensationPeriodLabel(
              dispensation.externalEfteruddanelseperiodeId,
              periodLabelLookup,
            )}
          />
          <DetailItem
            label="Antal lektioner"
            value={formatLessons(dispensation.numberOfLessons)}
          />
        </div>
      </div>
      {canEdit && (
        <EditDispensationModal
          dispensation={dispensation}
          periods={periods}
          userId={userId}
          onSave={handleSave}
        />
      )}
    </ModalProvider>
  );
};
